import { useState } from 'react';
import { useAppStore } from '@/store';
import { applyLUTToImageData, composeLUTWithICC } from '@/engine/export';
import { decodeTIFF, encodeTIFF, TIFFDecodeError } from '@/engine/tiff-codec';

// ---------------------------------------------------------------------------
// Apply DeviceLink to a raster image (TIFF / JPEG / PNG)
// ---------------------------------------------------------------------------

function ApplyToImage({ lut }: { lut: import('@/types').LUT3D }) {
  const [file, setFile]         = useState<File | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [done, setDone]         = useState(false);

  const isTIFF = (f: File) =>
    f.type === 'image/tiff' || /\.(tif|tiff)$/i.test(f.name);

  const handleApply = () => {
    if (!file) return;
    setApplying(true);
    setError(null);
    setDone(false);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const buf = reader.result as ArrayBuffer;

        // ---- TIFF path ----
        if (isTIFF(file)) {
          let imageData: ImageData;
          try {
            imageData = decodeTIFF(buf);
          } catch (e) {
            throw new Error(
              `TIFF decode failed: ${e instanceof TIFFDecodeError ? e.message : String(e)}`
            );
          }
          const corrected = applyLUTToImageData(imageData, lut);
          const outBuf    = encodeTIFF(corrected);
          const blob = new Blob([outBuf], { type: 'image/tiff' });
          const a = document.createElement('a');
          a.href     = URL.createObjectURL(blob);
          a.download = `corrected_${file.name.replace(/\.[^.]+$/, '')}.tif`;
          a.click();
          URL.revokeObjectURL(a.href);
          setApplying(false);
          setDone(true);
          return;
        }

        // ---- JPEG / PNG path (Canvas API) ----
        const blob0 = new Blob([buf], { type: file.type });
        const url   = URL.createObjectURL(blob0);
        const img   = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width  = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const corrected = applyLUTToImageData(imageData, lut);
            ctx.putImageData(corrected, 0, 0);

            // Re-encode as JPEG if source was JPEG, otherwise PNG
            const outMime = /\.(jpe?g)$/i.test(file.name) ? 'image/jpeg' : 'image/png';
            const ext     = outMime === 'image/jpeg' ? 'jpg' : 'png';
            canvas.toBlob(blob => {
              if (!blob) { setError('Failed to encode corrected image'); setApplying(false); return; }
              const a = document.createElement('a');
              a.href     = URL.createObjectURL(blob);
              a.download = `corrected_${file.name.replace(/\.[^.]+$/, '')}.${ext}`;
              a.click();
              URL.revokeObjectURL(a.href);
              setApplying(false);
              setDone(true);
            }, outMime, 0.95);
          } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            setApplying(false);
          } finally {
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => {
          setError('Failed to decode image');
          setApplying(false);
          URL.revokeObjectURL(url);
        };
        img.src = url;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setApplying(false);
      }
    };
    reader.onerror = () => { setError('Failed to read file'); setApplying(false); };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-200">Apply to RGB image</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Accepts TIFF (8/16-bit), JPEG, PNG. Output preserves the source format (TIFF → TIFF, JPEG → JPEG, other → PNG).
        </p>
      </div>

      <input
        type="file"
        accept="image/tiff,.tif,.tiff,image/jpeg,image/jpg,image/png"
        onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setDone(false); }}
        className="block w-full text-sm text-slate-400
                   file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0
                   file:bg-slate-700 file:text-slate-200 file:text-sm file:cursor-pointer
                   hover:file:bg-slate-600"
      />

      {file && (
        <p className="text-xs text-slate-500 font-mono">
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </p>
      )}

      {error && (
        <p className="text-xs text-red-400 font-mono bg-red-900/20 rounded px-3 py-2">{error}</p>
      )}
      {done && (
        <p className="text-xs text-emerald-400 font-mono">✓ Downloaded corrected image</p>
      )}

      <button
        onClick={handleApply}
        disabled={!file || applying}
        className="w-full py-2 rounded-lg bg-sky-500 hover:bg-sky-400
                   disabled:opacity-40 disabled:cursor-not-allowed
                   text-slate-900 font-semibold text-sm transition-colors"
      >
        {applying ? '⟳ Applying…' : '↓ Apply DeviceLink & Download'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compose DeviceLink into an existing ICC output profile
// ---------------------------------------------------------------------------

function ApplyToICC({ lut }: { lut: import('@/types').LUT3D }) {
  const [file, setFile]         = useState<File | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [done, setDone]         = useState(false);

  const handleApply = () => {
    if (!file) return;
    setApplying(true);
    setError(null);
    setDone(false);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const bytes  = new Uint8Array(reader.result as ArrayBuffer);
        const result = composeLUTWithICC(bytes, lut);
        if (!result.ok) {
          setError(result.error);
          setApplying(false);
          return;
        }
        const blob = new Blob([result.data.buffer as ArrayBuffer], { type: 'application/vnd.iccprofile' });
        const a = document.createElement('a');
        a.href     = URL.createObjectURL(blob);
        a.download = `corrected_${file.name.replace(/\.[^.]+$/, '')}.icm`;
        a.click();
        URL.revokeObjectURL(a.href);
        setApplying(false);
        setDone(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setApplying(false);
      }
    };
    reader.onerror = () => { setError('Failed to read ICC file'); setApplying(false); };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/50 p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-200">Compose into ICC output profile</h2>
        <p className="text-xs text-slate-500 mt-0.5">
          Upload an existing ICC output profile (.icc / .icm). The DeviceLink correction is baked
          into the AToB0 table and the corrected profile is downloaded.
          Requires a mft2 (lut16Type) AToB0 tag.
        </p>
      </div>

      <input
        type="file"
        accept=".icc,.icm,application/vnd.iccprofile"
        onChange={e => { setFile(e.target.files?.[0] ?? null); setError(null); setDone(false); }}
        className="block w-full text-sm text-slate-400
                   file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0
                   file:bg-slate-700 file:text-slate-200 file:text-sm file:cursor-pointer
                   hover:file:bg-slate-600"
      />

      {file && (
        <p className="text-xs text-slate-500 font-mono">
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </p>
      )}

      {error && (
        <p className="text-xs text-red-400 font-mono bg-red-900/20 rounded px-3 py-2">{error}</p>
      )}
      {done && (
        <p className="text-xs text-emerald-400 font-mono">✓ Downloaded corrected ICC profile</p>
      )}

      <button
        onClick={handleApply}
        disabled={!file || applying}
        className="w-full py-2 rounded-lg bg-sky-500 hover:bg-sky-400
                   disabled:opacity-40 disabled:cursor-not-allowed
                   text-slate-900 font-semibold text-sm transition-colors"
      >
        {applying ? '⟳ Composing…' : '↓ Compose & Download ICC'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ApplyView
// ---------------------------------------------------------------------------

export default function ApplyView() {
  const lut = useAppStore(s => s.session.lut);

  if (!lut) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 font-mono text-sm">
        Build a LUT first.
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Apply DeviceLink</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          {lut.gridSize}³ LUT · {lut.options.interpolation}
        </p>
      </div>

      <ApplyToImage lut={lut} />
      <ApplyToICC   lut={lut} />
    </div>
  );
}
